import React, { useState, useEffect } from 'react';
import { Truck, DollarSign, Clock, Globe, Save, RefreshCw, Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ShippingSettingsTab = () => {
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchRates();
  }, []);

  const fetchRates = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/shipping/rates/admin`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRates(res.data.rates || {});
    } catch (error) {
      toast.error('Failed to load shipping rates');
    } finally {
      setLoading(false);
    }
  };

  const updateRate = (region, method, field, value) => {
    setRates(prev => ({
      ...prev,
      [region]: {
        ...prev[region],
        [method]: {
          ...prev[region][method],
          [field]: field.includes('days') ? parseInt(value) || 0 : parseFloat(value) || 0
        }
      }
    }));
  };

  const updateRateName = (region, method, value) => {
    setRates(prev => ({
      ...prev,
      [region]: {
        ...prev[region],
        [method]: {
          ...prev[region][method],
          name: value
        }
      }
    }));
  };

  const addMethod = (region) => {
    const methodId = `custom_${Date.now()}`;
    setRates(prev => ({
      ...prev,
      [region]: {
        ...prev[region],
        [methodId]: {
          name: "New Shipping Method",
          base: 0,
          per_lb: 0,
          days_min: 1,
          days_max: 7
        }
      }
    }));
  };

  const removeMethod = (region, method) => {
    setRates(prev => {
      const newRegion = { ...prev[region] };
      delete newRegion[method];
      return { ...prev, [region]: newRegion };
    });
  };

  const saveRates = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_URL}/api/shipping/rates`, rates, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Shipping rates saved successfully!');
    } catch (error) {
      toast.error('Failed to save shipping rates');
    } finally {
      setSaving(false);
    }
  };

  const regionLabels = {
    US: { name: 'United States', icon: '🇺🇸' },
    CA: { name: 'Canada', icon: '🇨🇦' },
    INTL: { name: 'International', icon: '🌍' }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="shipping-settings">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Shipping Rates</h2>
          <p className="text-gray-500">Configure shipping costs by region and method</p>
        </div>
        <Button onClick={saveRates} disabled={saving} className="bg-[#003456]" data-testid="save-shipping-rates">
          <Save className="h-4 w-4 mr-2" />
          {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-700">
        <strong>How it works:</strong> Total shipping cost = Base Rate + (Per-Pound Rate × Product Weight)
      </div>

      {['US', 'CA', 'INTL'].map(region => (
        <div key={region} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="bg-gray-50 px-6 py-4 border-b flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl">{regionLabels[region].icon}</span>
              <div>
                <h3 className="font-semibold text-gray-900">{regionLabels[region].name}</h3>
                <p className="text-sm text-gray-500">{Object.keys(rates[region] || {}).length} shipping methods</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => addMethod(region)}
              data-testid={`add-method-${region}`}
            >
              <Plus className="h-4 w-4 mr-1" /> Add Method
            </Button>
          </div>

          <div className="p-6 space-y-4">
            {Object.entries(rates[region] || {}).map(([methodKey, method]) => (
              <div key={methodKey} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                <div className="flex items-center justify-between mb-4">
                  <Input
                    value={method.name}
                    onChange={(e) => updateRateName(region, methodKey, e.target.value)}
                    className="max-w-xs font-medium"
                    data-testid={`method-name-${region}-${methodKey}`}
                  />
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => removeMethod(region, methodKey)}
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    data-testid={`remove-method-${region}-${methodKey}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      <DollarSign className="h-3 w-3 inline mr-1" />
                      Base Rate ($)
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      value={method.base}
                      onChange={(e) => updateRate(region, methodKey, 'base', e.target.value)}
                      data-testid={`base-rate-${region}-${methodKey}`}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      <Truck className="h-3 w-3 inline mr-1" />
                      Per-Lb Rate ($)
                    </label>
                    <Input
                      type="number"
                      step="0.01"
                      value={method.per_lb}
                      onChange={(e) => updateRate(region, methodKey, 'per_lb', e.target.value)}
                      data-testid={`per-lb-rate-${region}-${methodKey}`}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      <Clock className="h-3 w-3 inline mr-1" />
                      Min Days
                    </label>
                    <Input
                      type="number"
                      value={method.days_min}
                      onChange={(e) => updateRate(region, methodKey, 'days_min', e.target.value)}
                      data-testid={`days-min-${region}-${methodKey}`}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      <Clock className="h-3 w-3 inline mr-1" />
                      Max Days
                    </label>
                    <Input
                      type="number"
                      value={method.days_max}
                      onChange={(e) => updateRate(region, methodKey, 'days_max', e.target.value)}
                      data-testid={`days-max-${region}-${methodKey}`}
                    />
                  </div>
                </div>

                <div className="mt-3 text-xs text-gray-500">
                  Example (65 lb product): ${(method.base + method.per_lb * 65).toFixed(2)}
                </div>
              </div>
            ))}

            {Object.keys(rates[region] || {}).length === 0 && (
              <div className="text-center py-8 text-gray-500">
                No shipping methods configured for this region.
                <Button variant="link" onClick={() => addMethod(region)}>Add one</Button>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ShippingSettingsTab;
